import dayjs from "dayjs";

type CalendarBar = {
  dataset: { date?: string };
  getBoundingClientRect(): { left: number; width: number };
  closest?(selector: string): CalendarBar | null;
};

type CalendarPointer = {
  clientX: number;
  currentTarget: CalendarBar;
};

/** A merged calendar bar may contain weekend filler cells without a job. */
export function resolveCalendarTarget<T extends { id?: number; phase?: { id?: number } }>(
  event: CalendarPointer,
  elements: Record<string, { element: T | null; type?: string }[]>,
  index: number,
  zoom: number,
): { date: string; element: T & { id: number; phase: { id: number } } } | null {
  // The registered child has 5px margins in day mode; dates follow the grid bar,
  // not the inset child's border or whichever icon the pointer happened to hit.
  const bar = event.currentTarget.closest?.('[data-calendar-bar]') ?? event.currentTarget;
  const start = bar.dataset.date;
  const { left, width } = bar.getBoundingClientRect();
  if (!start || !/^\d{4}-\d{2}-\d{2}$/.test(start) || dayjs(start).format("YYYY-MM-DD") !== start) return null;
  if (!Number.isFinite(zoom) || zoom <= 0 || !Number.isFinite(left) || !Number.isFinite(width) || width <= 0) return null;
  if (!Number.isFinite(event.clientX) || event.clientX < left || event.clientX > left + width || !Number.isInteger(index) || index < 0) return null;

  // The exact right border still belongs to the last visible day, never the next bar.
  const offset = Math.min(Math.floor((event.clientX - left) / zoom), Math.ceil(width / zoom) - 1);
  const date = dayjs(start).add(offset, "day").format("YYYY-MM-DD");
  const cell = elements?.[date]?.[index];
  const element = cell?.element;
  if (cell?.type === "filler" || !element || !Number.isInteger(element.id) || element.id! <= 0 || !Number.isInteger(element.phase?.id) || element.phase!.id! <= 0) return null;
  return { date, element: element as T & { id: number; phase: { id: number } } };
}

export function readPartDragData(dataTransfer: Pick<DataTransfer, "types" | "getData">) {
  const type = Array.from(dataTransfer.types).find((value) => value === "application/resource-booking" || value === "application/free-resource");
  if (!type) return null;
  const parts = dataTransfer.getData(type).split("#");
  const resourceBookingId = Number(parts[0]);
  const originDate = parts[1];
  if (!Number.isInteger(resourceBookingId) || resourceBookingId < 0) return null;
  // Existing bookings belong to a source day. Sidebar resources are weekly
  // availability entries without a date; their booking day comes from the drop target.
  if (type === "application/resource-booking" && (resourceBookingId === 0 || !/^\d{4}-\d{2}-\d{2}$/.test(originDate ?? "") || dayjs(originDate).format("YYYY-MM-DD") !== originDate)) return null;
  const resourceId = Number(parts[2] ?? "0");
  const resourceType = parts[3] ?? "NONE";
  if (type === "application/free-resource" && (resourceBookingId !== 0 || !Number.isInteger(resourceId) || resourceId <= 0 || !["HUMAN", "RESOURCE", "CREW"].includes(resourceType))) return null;
  return { resourceBookingId, originDate, resourceId, resourceType };
}
